// te-search.js
// Predictive Search + Main Search advanced query rewriting
// Adds: robust token normalization, red-carpet exclusion when searching "red",
// and hardware phrase exclusions when searching "gold"/"silver"/"gunmetal".

// 2026-08-26: rewriting DISABLED. Shopify stopped reliably parsing the
// field-scoped syntax on /search?q= — rewritten queries return the entire
// catalog, while plain queries behave correctly. Do not re-enable; if the
// red-carpet/hardware exclusions are needed again, implement them as Liquid
// render-time filters (like the vault filter in main-search.liquid) instead.
const TE_SEARCH_REWRITE_ENABLED = false;

// ------------------------------
// For Predictive Search
// ------------------------------
(function () {
  if (!TE_SEARCH_REWRITE_ENABLED) return;
  // Prevent double-patching if the script is evaluated more than once
  if (window.__predictiveFetchPatched) return;
  window.__predictiveFetchPatched = true;

  function buildAdvancedQuery(raw) {
    const cleaned = (raw || "").trim();
    if (!cleaned) return { q: "", q_display: "" };

    // ✅ Predictive-search UX: avoid rewriting super-short inputs (prevents "no results" flash)
    const MIN_LEN = 3;
    const cleanedForLen = cleaned.replace(/^"|"$/g, "");
    if (cleanedForLen.length < MIN_LEN) {
      // keep native predictive search behavior for short queries
      return { q: cleaned, q_display: cleaned };
    }

    // If the user already typed advanced syntax manually, keep it as-is
    const isAdvanced = cleaned.includes(":");
    if (isAdvanced) return { q: cleaned, q_display: cleaned };

    const terms = cleaned.match(/"[^"]+"|\S+/g) || [];
    const fields = ["title", "tag", "variants.title", "variants.sku"];

    // Normalize tokens so "red," "Red" "\"red\"" etc all behave consistently
    const norm = (t) =>
      String(t || "")
        .replace(/^"|"$/g, "") // strip wrapping quotes
        .trim()
        .toLowerCase()
        .replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, ""); // strip leading/trailing punctuation

    const normalizedTerms = terms.map(norm).filter(Boolean);

    const groups = terms
      .map((t) => t.trim())
      .filter(Boolean)
      .map((t) => {
        const ors = fields.map((f) => `${f}:${t}`).join(" OR ");
        return `(${ors})`;
      });

    // Existing exception: exclude "red carpet" only when searching for token "red"
    const EXCLUDED_TAG = "red carpet";
    const hasRedTerm = normalizedTerms.includes("red");

    let advancedQ = groups.join(" ");

    // Hardware exclusions: only apply when user searches the base color word
    // (This prevents "Gold Hardware" etc from being the sole reason the product matches.)
    if (normalizedTerms.includes("gold")) {
      advancedQ += ` -variants.title:"Gold Hardware"`;
    }
    if (normalizedTerms.includes("silver")) {
      advancedQ += ` -variants.title:"Silver Hardware"`;
    }
    if (normalizedTerms.includes("gunmetal")) {
      advancedQ += ` -variants.title:"Gunmetal Hardware"`;
    }

    if (hasRedTerm) {
      advancedQ += ` -tag:"${EXCLUDED_TAG}"`;
    }

    return { q: advancedQ, q_display: cleaned };
  }

  function shouldRewritePredictiveUrl(url) {
    const sectionId = url.searchParams.get("section_id");
    if (sectionId !== "predictive-search") return false;

    const path = url.pathname || "";
    return path.includes("/search");
  }

  const originalFetch = window.fetch;

  window.fetch = function (input, init) {
    try {
      let url;

      if (typeof input === "string") {
        url = new URL(input, window.location.origin);
      } else if (input && input.url) {
        url = new URL(input.url, window.location.origin);
      }

      if (url && shouldRewritePredictiveUrl(url)) {
        const raw = (url.searchParams.get("q") || "").trim();
        if (raw) {
          const { q, q_display } = buildAdvancedQuery(raw);

          if (q) {
            url.searchParams.set("q", q);
            url.searchParams.set("q_display", q_display);

            if (typeof input === "string") {
              input = url.toString();
            } else {
              // Preserve method/headers/etc where possible
              input = new Request(url.toString(), input);
            }
          }
        }
      }
    } catch (e) {
      // fail open
    }

    return originalFetch.call(this, input, init);
  };
})();

// ------------------------------
// Search results consolidation
// ------------------------------
// For queries containing a noise-filtered color word, the Liquid filter
// (snippets/te-search-filter.liquid) hides products per page, which leaves the
// server-rendered count and pagination reflecting the unfiltered result set.
// This fetches every result page via the Section Rendering API (supported,
// documented behavior — no query rewriting), merges the surviving product
// cards into one grid, sets the count to the real number, and removes the
// pagination. The count is rendered hidden by main-search.liquid for these
// queries and revealed here; on any failure both are revealed unchanged.
document.addEventListener("DOMContentLoaded", () => {
  // Keep in sync with the trigger words in snippets/te-search-filter.liquid
  // and te_triggers in sections/main-search.liquid.
  const TRIGGERS = ["red", "blue", "gold", "silver", "gunmetal"];
  const PAGE_SIZE = 48;
  const MAX_PAGES = 15;

  if (!window.location.pathname.includes("/search")) return;

  const params = new URLSearchParams(window.location.search);
  const q = (params.get("q") || "").trim().toLowerCase();
  if (!q) return;

  const words = q.split(/\s+/);
  if (!TRIGGERS.some((t) => words.includes(t))) return;

  const countEl = document.querySelector("[data-te-results-count]");
  const currentPage = parseInt(params.get("page") || "1", 10);
  if (currentPage > 1) {
    // Landed mid-pagination (old link); merging would duplicate this page's
    // cards. Fall back to the server-rendered count and pagination.
    if (countEl) countEl.style.visibility = "";
    const pag = document.querySelector("[data-te-pagination]");
    if (pag) pag.style.visibility = "";
    return;
  }
  const paginationEl = document.querySelector("[data-te-pagination]");
  const grid = document.querySelector('product-list[id^="product-list-"]');

  const revealUnchanged = () => {
    if (countEl) countEl.style.visibility = "";
    if (paginationEl) paginationEl.style.visibility = "";
  };

  const rawCount = parseInt((countEl?.textContent || "").replace(/[^\d]/g, ""), 10);
  if (!grid || !rawCount) {
    revealUnchanged();
    return;
  }

  const sectionId = grid.id.replace("product-list-", "");
  const totalPages = Math.min(Math.ceil(rawCount / PAGE_SIZE), MAX_PAGES);

  const finish = () => {
    const shown = grid.children.length;
    if (countEl) {
      countEl.textContent = `${shown} ${shown === 1 ? "result" : "results"}`;
      countEl.style.visibility = "";
    }
    if (paginationEl) paginationEl.remove();
  };

  if (totalPages <= 1) {
    finish();
    return;
  }

  const fetchPageCards = (page) => {
    const url = new URL(window.location.href);
    url.searchParams.set("page", page);
    url.searchParams.set("section_id", sectionId);
    return fetch(url.toString())
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error("HTTP " + r.status))))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const pageGrid = doc.querySelector('product-list[id^="product-list-"]');
        return Array.from(pageGrid ? pageGrid.children : []);
      });
  };

  const pageNumbers = [];
  for (let page = 2; page <= totalPages; page++) pageNumbers.push(page);

  Promise.all(pageNumbers.map(fetchPageCards))
    .then((pages) => {
      pages.forEach((cards) => {
        cards.forEach((card) => {
          const node = document.importNode(card, true);
          // The list's reveal animation already ran; without this the cards
          // keep the pre-animation opacity:0 and leave blank space.
          [node, ...node.querySelectorAll("[reveal-on-scroll]")].forEach((el) => el.removeAttribute("reveal-on-scroll"));
          grid.appendChild(node);
        });
      });
      finish();
    })
    .catch(() => {
      // fail open: keep server-rendered count and pagination
      revealUnchanged();
    });
});

// ------------------------------
// For Main Search
// ------------------------------
document.addEventListener("DOMContentLoaded", () => {
  if (!TE_SEARCH_REWRITE_ENABLED) return;
  const forms = document.querySelectorAll('form[action*="/search"]');

  forms.forEach((form) => {
    form.addEventListener("submit", (e) => {
      const qInput = form.querySelector('input[name="q"]');
      if (!qInput) return;

      const raw = (qInput.value || "").trim();
      if (!raw) return;

      // If the user already typed advanced syntax manually, keep it as-is but still set q_display if possible.
      const isAdvanced = raw.includes(":");

      // Build the "advanced" q only when needed
      let advancedQ = raw;

      if (!isAdvanced) {
        const terms = raw.match(/"[^"]+"|\S+/g) || [];
        const fields = ["title", "tag", "variants.title", "variants.sku"];

        const norm = (t) =>
          String(t || "")
            .replace(/^"|"$/g, "")
            .trim()
            .toLowerCase()
            .replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, "");

        const normalizedTerms = terms.map(norm).filter(Boolean);

        const groups = terms
          .map((t) => t.trim())
          .filter(Boolean)
          .map((t) => {
            const ors = fields.map((f) => `${f}:${t}`).join(" OR ");
            return `(${ors})`;
          });

        const EXCLUDED_TAG = "red carpet";
        const hasRedTerm = normalizedTerms.includes("red");

        advancedQ = groups.join(" ");

        // Hardware exclusions: only apply when user searches the base color word
        if (normalizedTerms.includes("gold")) {
          advancedQ += ` -variants.title:"Gold Hardware"`;
        }
        if (normalizedTerms.includes("silver")) {
          advancedQ += ` -variants.title:"Silver Hardware"`;
        }
        if (normalizedTerms.includes("gunmetal")) {
          advancedQ += ` -variants.title:"Gunmetal Hardware"`;
        }

        // Only exclude "red carpet" when the user searched for "red"
        if (hasRedTerm) {
          advancedQ += ` -tag:"${EXCLUDED_TAG}"`;
        }
      }

      // Force navigation with both q and q_display (reliable even if theme normally hijacks submit)
      e.preventDefault();

      const url = new URL(form.action, window.location.origin);

      // Override q and set q_display (raw user input for display)
      url.searchParams.set("q", advancedQ);
      url.searchParams.set("q_display", raw);

      // Preserve any existing fields (type=product, etc) EXCEPT q/q_display
      const fd = new FormData(form);
      for (const [key, value] of fd.entries()) {
        if (key === "q" || key === "q_display") continue;
        if (value != null && String(value).length) url.searchParams.set(key, value);
      }

      window.location.href = url.toString();
    });
  });
});
